import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Cancel, Delete, Save } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import {
  EntityTypes,
  FileTag,
  FileType,
  FileUpload,
  FormMode,
  Property,
  PropertyInstruction,
} from 'types';
import { cardHeaderSx, cardSx, mobileCardSx } from 'utils/styleProps';
import { MAX_FILE_SIZE_BYTES } from 'utils';
import { AppContext } from 'context/appContext';
import { FileContext } from 'context/fileUploadContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import AuturaTextField from 'components/inputs/AuturaTextField';
import FileDropzone from 'components/inputs/FileDropzone';
import { FileRejection } from 'react-dropzone';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

interface InstructionDialogProps {
  open: boolean;
  instruction: PropertyInstruction;
  setInstruction: (instruction: PropertyInstruction) => void;
  formMode: FormMode;
  handleCancel: () => void;
  handleSubmit: (fileKey: string) => void;
  entity: Property;
}

export const InstructionDialog = ({
  open,
  instruction,
  setInstruction,
  formMode,
  handleCancel,
  handleSubmit,
  entity,
}: InstructionDialogProps): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints, palette } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { state } = useContext(AppContext);
  const {
    addFiles,
    files: contextFiles,
    removeFile,
    deleteFile,
  } = useContext(FileContext);
  const displayToast = useContext(ToastContext);

  const [fileKey, setFileKey] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingFile, setUploadingFile] = useState<FileUpload | null>(null);
  const [dropzoneState, setDropzoneState] = useState<
    'ready' | 'loading' | 'completed'
  >('ready');

  // Initialize image preview when dialog opens or instruction changes
  useEffect(() => {
    if (open) {
      setFileKey('');
      setImagePreview(instruction.url || '');
      setUploadingFile(null);
      setDropzoneState('ready');
    }
  }, [open, instruction.id]);

  // Track file upload progress
  useEffect(() => {
    if (!uploadingFile) return;
    const match = contextFiles.find((f) => f.preview === uploadingFile.preview);
    if (!match) return;
    if (match.uploaded && match.metadata.fileKey) {
      setFileKey(match.metadata.fileKey);
      setDropzoneState('completed');
      setUploadingFile(null);
    } else if (match.metadata.errorMsg) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: match.metadata.errorMsg,
      });
      setDropzoneState('ready');
      setUploadingFile(null);
    }
  }, [contextFiles, uploadingFile]);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      if (uploadingFile) {
        URL.revokeObjectURL(uploadingFile.preview);
      }
    };
  }, [uploadingFile]);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles?.length) {
        rejectedFiles.forEach((r) =>
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: r.errors[0].message,
          }),
        );
      }
      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];
      setDropzoneState('loading');
      const fileUpload = Object.assign(file, {
        preview: URL.createObjectURL(file),
        metadata: {
          createdByUserID: state.user.id,
          tenantID: state.tenant.id,
          entityType: EntityTypes.Property,
          entityID: Number(entity.id),
          fileType: FileType.Image,
          data: {
            title: file.name,
            description: '',
            fileTag: FileTag.InstructionPhoto,
          },
        },
        uploaded: false,
      }) as FileUpload;
      setImagePreview(fileUpload.preview);
      setUploadingFile(fileUpload);
      addFiles([fileUpload]);
    },
    [state, entity, addFiles],
  );

  const handleFileClear = () => {
    if (uploadingFile) {
      removeFile(uploadingFile);
      URL.revokeObjectURL(uploadingFile.preview);
    }
    setUploadingFile(null);
    setFileKey('');
    setDropzoneState('ready');
    setImagePreview(instruction.url || '');
  };

  const handleImageRemove = () => {
    if (fileKey) {
      deleteFile(fileKey);
    }
    setImagePreview('');
    setFileKey('');
    setDropzoneState('ready');
  };

  const onCancel = () => {
    if (uploadingFile) {
      removeFile(uploadingFile);
      URL.revokeObjectURL(uploadingFile.preview);
      setUploadingFile(null);
    }
    if (fileKey) {
      deleteFile(fileKey);
    }
    handleCancel();
  };

  const onSubmit = () => {
    handleSubmit(fileKey);
  };

  const title = useMemo(() => {
    if (formMode === 'create') {
      return t('buttons.create', {
        name: t('instruction.label', { ns: 'entities' }),
      });
    }
    return t('buttons.update', {
      name: t('instruction.label', { ns: 'entities' }),
    });
  }, [formMode, t]);

  const isSaveDisabled =
    !instruction.title.trim() ||
    !instruction.text.trim() ||
    dropzoneState === 'loading';
  const instructionLabel = t('instruction.label', { ns: 'entities' });

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      aria-label={title}
      fullScreen={mobile}
      maxWidth={'sm'}
      sx={{ mt: { xs: -1, lg: 0 } }}
    >
      <ValidatorForm onSubmit={onSubmit}>
        <Card raised sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: 'flex',
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={'h4'}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: 'left',
                }}
              >
                {title}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, flexGrow: 1 }}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid size={12}>
                <AuturaTextField
                  name='title'
                  label={t('instruction.title', { ns: 'entities' })}
                  value={instruction.title}
                  onChange={(e) =>
                    setInstruction({ ...instruction, title: e.target.value })
                  }
                  maxLength={64}
                  helperText={t('helpers.instruction_title_helper', {
                    ns: 'rules',
                  })}
                  required
                />
              </Grid>
              <Grid size={12}>
                {imagePreview && !uploadingFile ? (
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      '&:hover .image-remove-btn': { opacity: 1 },
                    }}
                  >
                    <Box
                      component='img'
                      src={imagePreview}
                      alt={instruction.title || instructionLabel}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      className='image-remove-btn'
                      size='small'
                      onClick={handleImageRemove}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'error.light', color: 'white' },
                        color: 'error.main',
                      }}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </Box>
                ) : (
                  <FileDropzone
                    onDrop={handleFileDrop}
                    accept={{
                      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
                    }}
                    maxSize={MAX_FILE_SIZE_BYTES}
                    state={dropzoneState}
                    onCancel={handleFileClear}
                    caption={t('labels.filezone', { ns: 'rules' })}
                  />
                )}
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  name='text'
                  label={t('instruction.text', { ns: 'entities' })}
                  value={instruction.text}
                  onChange={(e) =>
                    setInstruction({ ...instruction, text: e.target.value })
                  }
                  maxLength={254}
                  multiline
                  rows={5}
                  required
                  helperText={t('helpers.instruction_text_helper', {
                    ns: 'rules',
                  })}
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  width='100%'
                  direction='row'
                  spacing={2}
                  justifyContent='flex-end'
                  mt={2}
                  display='flex'
                  alignItems='flex-end'
                  alignContent='flex-end'
                >
                  <Button
                    variant={'outlined'}
                    onClick={onCancel}
                    color={'secondary'}
                    endIcon={<Cancel />}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant={'contained'}
                    type='submit'
                    color={'secondary'}
                    endIcon={<Save />}
                    disabled={isSaveDisabled}
                  >
                    {t('submit')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};
